/**
 * One envelope in the workbench: subject / status / signer progress and every
 * action that used to need the DocuSign dropdown.
 */
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight, History, Download, Award, PenSquare, BellRing, FileSignature, Trash2, Loader2, MoreHorizontal, CheckCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { EnvelopeActions } from "@/pages/legal/EnvelopeActions";
import { fetchEnvelopePdf, fetchEnvelopeCertificate, discardDraftEnvelope } from "@/lib/api";
import { STATUS_TONE, fmtDate, daysAgo, signerSummary, isInFlight, downloadBlob } from "./envelopeUtils";

const SignerDots = ({ signers }) => (
  <div className="flex items-center gap-1" data-testid="signer-dots">
    {signers.map((s, i) => {
      const tone = s.signed_at ? "bg-emerald-500" : s.status === "declined" ? "bg-rose-500" : s.delivered_at ? "bg-sky-500" : "bg-slate-300";
      const label = s.signed_at ? "Signed" : s.status === "declined" ? "Declined" : s.delivered_at ? "Viewed" : "Pending";
      return (
        <Tooltip key={i}>
          <TooltipTrigger asChild><span className={`w-2.5 h-2.5 rounded-full ${tone}`} /></TooltipTrigger>
          <TooltipContent className="text-xs">{s.name} — {label}</TooltipContent>
        </Tooltip>
      );
    })}
  </div>
);

export const EnvelopeRow = ({ env, locale, onHistory, onCorrectView, onNotification, onResume, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);
  const sum = signerSummary(env);
  const age = daysAgo(env.sent_at);
  const stale = isInFlight(env) && age !== null && age >= 7;

  const dl = async (kind) => {
    setBusy(kind);
    try {
      const short = env.envelope_id.slice(0, 8);
      if (kind === "pdf") downloadBlob(await fetchEnvelopePdf(env.envelope_id), `${env.company_name || "envelope"}_${short}.pdf`);
      if (kind === "pdfcert") downloadBlob(await fetchEnvelopePdf(env.envelope_id, { certificate: true }), `${env.company_name || "envelope"}_${short}_with_certificate.pdf`);
      if (kind === "cert") downloadBlob(await fetchEnvelopeCertificate(env.envelope_id), `${env.company_name || "envelope"}_${short}_certificate.pdf`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard this draft envelope? It is deleted in DocuSign.")) return;
    setBusy("discard");
    try { await discardDraftEnvelope(env.envelope_id); toast.success("Draft discarded"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Discard failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className={`rounded-lg border bg-white transition-colors ${stale ? "border-amber-300" : "border-slate-200 hover:border-slate-300"}`} data-testid={`env-row-${env.envelope_id}`}>
      <div className="p-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <button type="button" onClick={() => setExpanded((v) => !v)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid={`env-expand-${env.envelope_id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate" title={env.subject}>{env.subject}</span>
            <Badge className={`shrink-0 text-[10px] ${STATUS_TONE[env.status] || "bg-slate-500 text-white"}`} data-testid={`env-status-${env.envelope_id}`}>{env.status_label}</Badge>
            {env.application_id && <span className="text-[10px] font-mono text-slate-500">{env.application_id}</span>}
            {(env.document_types || []).map((t) => <span key={t} className="text-[10px] uppercase tracking-wide text-slate-500 border rounded px-1">{t}</span>)}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
            <span>{env.sent_at ? `Sent ${fmtDate(env.sent_at, locale)}` : `Created ${fmtDate(env.created_at, locale)}`}{age !== null && isInFlight(env) ? ` · ${age}d` : ""}</span>
            <span className="text-slate-300">•</span>
            <SignerDots signers={env.signers || []} />
            <span>
              {sum.signed > 0 && <span className="text-emerald-600">{sum.signed} signed</span>}
              {sum.signed > 0 && (sum.viewed > 0 || sum.pending > 0) && ", "}
              {sum.viewed > 0 && <span className="text-sky-600">{sum.viewed} viewed</span>}
              {sum.viewed > 0 && sum.pending > 0 && ", "}
              {sum.pending > 0 && <span>{sum.pending} pending</span>}
              {sum.declined > 0 && <span className="text-rose-600"> · {sum.declined} declined</span>}
            </span>
            {stale && <span className="text-amber-700 font-medium">· waiting {age} days</span>}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 w-full md:w-auto justify-end flex-wrap" onClick={(e) => e.stopPropagation()}>
          <EnvelopeActions envelope={env} onChanged={onChanged} />
          {env.status === "created" && (
            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onResume(env)} data-testid={`env-resume-${env.envelope_id}`}>
              <FileSignature className="w-3 h-3 mr-1" /> Resume draft
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onHistory(env)} data-testid={`env-history-${env.envelope_id}`}>
            <History className="w-3 h-3 mr-1" /> History
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-1.5" aria-label="More" data-testid={`env-more-${env.envelope_id}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MoreHorizontal className="w-3.5 h-3.5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs w-64">
              <DropdownMenuItem onClick={() => dl("pdf")} data-testid={`env-dl-pdf-${env.envelope_id}`}><Download className="w-3.5 h-3.5 mr-2" /> Download documents (PDF){env.status !== "completed" && " — as signed so far"}</DropdownMenuItem>
              {env.status === "completed" && (
                <>
                  <DropdownMenuItem onClick={() => dl("pdfcert")} data-testid={`env-dl-pdfcert-${env.envelope_id}`}><CheckCircle className="w-3.5 h-3.5 mr-2" /> Signed PDF + certificate</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => dl("cert")} data-testid={`env-dl-cert-${env.envelope_id}`}><Award className="w-3.5 h-3.5 mr-2" /> Certificate of completion only</DropdownMenuItem>
                </>
              )}
              {isInFlight(env) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onCorrectView(env)} data-testid={`env-correct-view-${env.envelope_id}`}><PenSquare className="w-3.5 h-3.5 mr-2" /> Correct documents &amp; fields (DocuSign view)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onNotification(env)} data-testid={`env-notification-${env.envelope_id}`}><BellRing className="w-3.5 h-3.5 mr-2" /> Reminders &amp; expiry</DropdownMenuItem>
                </>
              )}
              {env.status === "created" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-rose-600" onClick={discard} data-testid={`env-discard-${env.envelope_id}`}><Trash2 className="w-3.5 h-3.5 mr-2" /> Discard draft</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3">
          <div className="rounded-md border bg-slate-50">
            <div className="px-3 py-1.5 border-b text-[11px] font-medium text-slate-600 grid grid-cols-12 gap-2">
              <span className="col-span-4">Signer</span><span className="col-span-4">Email</span><span className="col-span-2">Status</span><span className="col-span-2">Date</span>
            </div>
            {(env.signers || []).length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No signer details</div>}
            {(env.signers || []).map((s, i) => {
              const st = s.signed_at ? "Signed" : s.delivered_at ? "Viewed" : s.status === "declined" ? "Declined" : s.status === "sent" ? "Sent" : "Pending";
              return (
                <div key={i} className="px-3 py-1.5 text-sm grid grid-cols-12 gap-2 items-center border-b last:border-b-0">
                  <span className="col-span-4 truncate font-medium" title={s.name}>{s.name}</span>
                  <span className="col-span-4 truncate text-muted-foreground" title={s.email}>{s.email}</span>
                  <span className={`col-span-2 text-xs font-medium flex items-center gap-1 ${st === "Signed" ? "text-emerald-600" : st === "Viewed" ? "text-sky-600" : st === "Declined" ? "text-rose-600" : "text-slate-500"}`}>
                    {st === "Signed" && <CheckCircle className="w-3 h-3" />}{st === "Viewed" && <Eye className="w-3 h-3" />}{st}
                  </span>
                  <span className="col-span-2 text-xs text-muted-foreground">{fmtDate(s.signed_at || s.delivered_at, locale)}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground font-mono">{env.envelope_id}{env.completed_at ? ` · completed ${fmtDate(env.completed_at, locale, true)}` : ""}</p>
        </div>
      )}
    </div>
  );
};
